import {
	ReactElement,
	ReadonlyRefObject,
	Ref,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	MouseEvent
} from "react";
import {
	KeyEventArguments,
	TextInput,
	TextInputHandle
} from "../TextInput/TextInput";
import { DropOver } from "../DropOver/DropOver";
import { ActionListBox } from "../ActionListBox/ActionListBox";
import { ActionList } from "../ActionList/ActionList";
import { ActionListItem } from "../ActionList/ActionListItem";
import { DropOverInput, DropOverInputAction } from "../DropOverInput/DropOverInput";
import textInputStyles from "../TextInput/TextInput.module.less";
import {
	setTrackingId,
	generateTrackingId,
	isNil,
	isNotNil,
	ensureNotNil
} from "client/Misc/utils";
import { mergeRefs } from "client/Misc/reactUtils";
import { findLastIndex } from "client_legacy/ndaw/arrayHelpers";
import classNames from "classnames";
import { getUtcTimeZone, TimeZone } from "client/DateAndTime/timezones";
import { getCurrentCulture, localize } from "client_legacy/ndaw/culture";
import { TimeOnly } from "client/DateAndTime/timeOnly";
import {
	KeyBoardListNavigationContext,
	useKeyboardListNavigation,
	useKeyboardListNavigationItem,
	useResizeObserver
} from "client/Hooks";
import { DateTime } from "luxon";
import { UniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import { Key } from "client/Misc/commonTypes";

const LUXON_TIME_FORMAT = DateTime.TIME_SIMPLE;
const utcTimezone: TimeZone = getUtcTimeZone(getCurrentCulture());

const defaultTimePlaceholder: string = formatTimeOnlyUsingLuxon(
	new TimeOnly(0, 0, 0, 0),
	LUXON_TIME_FORMAT
);

export type BaseTimePickerProps = {
	value: TimeOnly | null;
	onChange: (newDate: TimeOnly | null) => void;
	shouldRenderNative: boolean;
	trackingId: string;
	isInvalid?: boolean;
	isDisabled?: boolean;
	placeholder?: string;
	timeListOptions: readonly TimeOnly[];
	dropDownWidth?: "fill" | "content";
};

export const BaseTimePicker = (props: BaseTimePickerProps): ReactElement => {
const {
		value,
		onChange,
		shouldRenderNative,
		trackingId,
		isInvalid,
		isDisabled,
		placeholder = defaultTimePlaceholder,
		timeListOptions,
		dropDownWidth = "normal"
	} = props;

	const [isDropOverOpen, setIsDropOverOpen] = useState<boolean>(false);
	const [rawValue, setRawValue] = useState<RawValueWithParsedTimeOnly>({
		raw: "",
		parsed: null
	});

	const [isEditingText, setIsEditingText] = useState<boolean>(false);
	const [hasFocus, setHasFocus] = useState<boolean>(false);

	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const textInputRef: RefObject<TextInputHandle | null> = useRef<TextInputHandle>(null);
	const itemRefs: ReadonlyRefObject<Map<string, HTMLLIElement | null>> = useRef<Map<string, HTMLLIElement | null>>(new Map());
	const ignoreFocusEventRef: RefObject<boolean> = useRef<boolean>(false);
	const { id } = useContext(UniqueInputIdContext);
	const { width } = useResizeObserver(ref);

	useEffect(
		(): void => {
			if (isEditingText || value?.isInvalid()) {
				return;
			}

			setRawValue(
				rawValue => {
					if (areTimesEqual(value, rawValue.parsed)) {
						return rawValue;
					}

					return {
						raw: isNotNil(value)
							? formatTimeOnlyUsingLuxon(
								value,
								shouldRenderNative ? "native" : LUXON_TIME_FORMAT
							)
							: "",
						parsed: value
					};
				}
			);
		},
		[
			isEditingText,
			shouldRenderNative,
			value
		]
	);

	const setItemRef = useCallback(
		(item: HTMLLIElement | null, key: string): void => {
			if (isNil(item)) {
				itemRefs.current
					.delete(key);

				return;
			}

			itemRefs.current
				.set(key, item);
		},
		[]
	);

	const scrollTargetKey = useMemo(
		(): string | null => {
			if (!value || value.isInvalid() || timeListOptions.length === 0) {
				return null;
			}

			const valueIso: string = value.toISOString();

			const targetIndex: number = findLastIndex(
				timeListOptions,
				(option: TimeOnly): boolean => option.toISOString() <= valueIso
			);

			if (targetIndex === -1) {
				return timeListOptions[0].toISOString();
			}

			return timeListOptions[targetIndex].toISOString();
		},
		[value, timeListOptions]
	);

	useEffect(
		(): void => {
			if (!isDropOverOpen || scrollTargetKey === null) {
				return;
			}

			itemRefs.current
				.get(scrollTargetKey)
				?.scrollIntoView({ block: "start" });
		},
		[isDropOverOpen, scrollTargetKey]
	);

	const textInputChangeHandler = (newTextValue: string): void => {
		const newTimeValue: TimeOnly | null = parseTimeOnlyUsingLuxon(
			newTextValue,
			shouldRenderNative
		);

		setIsEditingText(true);
		setRawValue({
			raw: newTextValue,
			parsed: newTimeValue
		});
	};

	const textInputBlurHandler = (): void => {
		setIsEditingText(false);
		onChange(rawValue.parsed);
	};

	const focusInputHandlerWithoutChangingDropOverState = useCallback(
		(): void => {
			ignoreFocusEventRef.current = true;

			textInputRef.current
				?.focus();

			ignoreFocusEventRef.current = false;
		},
		[]
	);

	const textInputFocusHandler = useCallback(
		(): void => {
			if (ignoreFocusEventRef.current) {
				return;
			}

			setIsDropOverOpen(true);
		},
		[]
	);

	const actions = useMemo(
		(): readonly DropOverInputAction[] => {
			return [
				{
					icon: "clock",
					onClick: () => {
						setIsDropOverOpen(isOpen => !isOpen);
						focusInputHandlerWithoutChangingDropOverState();
					}
				}
			];
		},
		[focusInputHandlerWithoutChangingDropOverState]
	);

	const timeDropOverOutsideIgnoreRefs = useMemo(
		(): readonly RefObject<HTMLDivElement | null>[] => [ref],
		[]
	);

	const timeListChangeHandler = useCallback(
		(time: TimeOnly): void => {
			setIsEditingText(false);
			onChange(time);
			textInputRef.current
				?.focus();
			setIsDropOverOpen(false);
		},
		[onChange]
	);

	const preventStealingFocusHandler = useCallback(
		(e: MouseEvent): void => {
			e.preventDefault();
		},
		[]
	);

	const closeSuggestionsHandler = useCallback(
		(): void => {
			setIsDropOverOpen(false);

			focusInputHandlerWithoutChangingDropOverState();
		},
		[focusInputHandlerWithoutChangingDropOverState]
	);

	const {
		contextData: keyboardNavigationContext,
		focusFirstItemHandler
	} = useKeyboardListNavigation({
		onFocusInput: focusInputHandlerWithoutChangingDropOverState,
		onCancel: closeSuggestionsHandler,
		shouldCancelDirectlyFromItems: true
	});

	const textInputKeyDownHandler = useCallback(
		(args: KeyEventArguments): boolean => {
			switch (args.key) {
				case Key.ArrowDown:
				case Key.Enter:
					setIsDropOverOpen(true);

					// Delay to ensure DropOver is rendered before focusing first item
					setTimeout(
						(): void => {
							focusFirstItemHandler();
						},
						1
					);

					return true;
				case Key.Escape:
					closeSuggestionsHandler();
					return true;
				case Key.Tab:
					closeSuggestionsHandler();
					return false;
				default:
					return false;
			}
		},
		[
			focusFirstItemHandler,
			closeSuggestionsHandler
		]
	);

	if (shouldRenderNative) {
		return (
			<input
				{...setTrackingId(trackingId)}
				id={id}
				type="time"
				className={
					classNames(
						textInputStyles.root,
						{
							[textInputStyles.root_isInvalid]: isInvalid
						}
					)
				}
				value={rawValue.raw}
				onChange={(e) => textInputChangeHandler(e.target.value)}
				onBlur={textInputBlurHandler}
				disabled={isDisabled}
			/>
		);
	}

	return (
		<>
			<div
				ref={ref}
				onFocus={() => setHasFocus(true)}
				onBlur={() => setHasFocus(false)}
			>
				<div
					onClick={() => setIsDropOverOpen(true)}
				>
					<DropOverInput
						isActive={hasFocus}
						actions={actions}
						isInvalid={isInvalid}
						isDisabled={isDisabled}
					>
						<TextInput
							onFocus={textInputFocusHandler}
							value={rawValue.raw}
							isBorderless
							height="fill"
							onChange={(s) => textInputChangeHandler(s)}
							onBlur={textInputBlurHandler}
							attachedOn="right"
							trackingId={trackingId}
							isDisabled={isDisabled}
							placeholder={placeholder}
							ref={textInputRef}
							onKeyDown={textInputKeyDownHandler}
						/>
					</DropOverInput>
				</div>
				{isDropOverOpen
					&& !isDisabled
					&& (
						<DropOver
							target={ensureNotNil(ref.current)}
							targetAlignment="br"
							contentAlignment="tr"
							onClose={closeSuggestionsHandler}
							outsideClickIgnoreRefs={timeDropOverOutsideIgnoreRefs}
							closeOnMouseDown
						>
							<ActionListBox
								isHeightConstrainted
								menuTitle={localize("_Time")}
								trackingId={generateTrackingId(trackingId, "__wrapper")}
								width={dropDownWidth === "fill" ? width : undefined}
								onMouseDown={preventStealingFocusHandler}
							>
								<KeyBoardListNavigationContext.Provider value={keyboardNavigationContext}>
									<ActionList
										trackingId={generateTrackingId(trackingId, "__list")}
										width="fill"
									>
										{timeListOptions.map(
											(x: TimeOnly, i: number): ReactElement => {
												const key: string = x.toISOString();

												return (
													<TimeListItem
														key={key}
														ref={(item) => setItemRef(item, key)}
														text={formatTimeOnlyUsingLuxon(
															x,
															LUXON_TIME_FORMAT
														)}
														onSelect={() => timeListChangeHandler(x)}
														index={i}
													/>
												);
											}
										)}
									</ActionList>
								</KeyBoardListNavigationContext.Provider>
							</ActionListBox>
						</DropOver>
					)
				}
			</div>
		</>
	);
};
BaseTimePicker.displayName = "BaseTimePickerProps";

type TimeListItemProps = {
	index: number;
	text: string;
	onSelect: () => void;
	ref?: Ref<HTMLLIElement>;
};

const TimeListItem = (props: TimeListItemProps): ReactElement => {
	const {
		index,
		text,
		onSelect,
		ref
	} = props;

	const {
		onKeyDown,
		ref: keyboardNavigationRef
	} = useKeyboardListNavigationItem({
		itemIndex: index,
		onExecute: onSelect,
		executeKey: [
			Key.Enter,
			Key.Space,
			Key.Tab
		]
	});

	return (
		<ActionListItem
			ref={mergeRefs(keyboardNavigationRef, ref ?? null)}
			onKeyDown={onKeyDown}
			text={text}
			onClick={onSelect}
			trackingId={generateTrackingId("value", `|${text}`)}
			shouldNotAutoCloseContainingDropOver
			tabIndex={-1}
			bubbleClick={false}
		/>
	);
};
TimeListItem.displayName = "TimeListItem";

function areTimesEqual(time1: TimeOnly | null, time2: TimeOnly | null): boolean {
	if (!time1 && !time2) {
		return true;
	}

	if (!time1 || !time2) {
		return false;
	}

	return time1.isEqualTo(time2);
}

function formatTimeOnlyUsingLuxon(
	timeOnly: TimeOnly,
	format: "native" | Intl.DateTimeFormatOptions
): string {
	const dateTime: DateTime = DateTime.fromObject(
		{
			year: 2000,
			month: 1,
			day: 1,
			hour: timeOnly.hour,
			minute: timeOnly.minute,
			second: timeOnly.second,
			millisecond: timeOnly.millisecond
		},
		{
			zone: utcTimezone.identifier
		}
	);

	return format === "native"
		? ensureNotNil(
			dateTime.toFormat("HH:mm")

		)
		: dateTime.toLocaleString(format);
}

function parseTimeOnlyUsingLuxon(
	value: string,
	isIso: boolean
): TimeOnly | null {
	if (!value) {
		return null;
	}

	const luxonValue: DateTime = isIso
		? DateTime.fromISO(
			value,
			{
				zone: utcTimezone.identifier
			}
		)
		: DateTime.fromFormat(
			value,
			// Luxon doesn't provide a utility function to parse from Intl.DateTimeFormatOptions
			// we are using token formats for now to remediate to that
			"t",
			{
				zone: utcTimezone.identifier
			}
		);

	if (!luxonValue.isValid) {
		return TimeOnly.InvalidValue;
	}

	return new TimeOnly(
		luxonValue.hour,
		luxonValue.minute,
		luxonValue.second,
		luxonValue.millisecond
	);
}

type RawValueWithParsedTimeOnly = {
	raw: string;
	parsed: TimeOnly | null;
};